import React from 'react';
import { Box, SpaceBetween, Badge } from '@cloudscape-design/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Parse structured response into sections - handles partial content during streaming
 */
const parseStructuredResponse = (content, isStreaming = false) => {
    const sections = [];
    
    // Split by h3 headings
    const parts = content.split(/###\s+/);
    
    // Check if there's content before the first heading (excluding Redis cache indicator)
    // Redis cache indicator is now handled separately in RIDAnalyzer.jsx
    if (parts[0] && parts[0].trim()) {
        const preamble = parts[0].trim();
        // Only add preamble if it's not just the Redis cache indicator
        if (!preamble.match(/^⚡\s*\*\*\[Redis Cache\]\*\*/)) {
            sections.push({ 
                title: null, 
                body: preamble, 
                isComplete: true,
                isPreamble: true 
            });
        }
    }
    
    for (let i = 1; i < parts.length; i++) {
        const part = parts[i];
        const lines = part.split('\n');
        const title = lines[0].trim();
        const body = lines.slice(1).join('\n').trim();
        
        // Only include sections with content, or the last section if streaming
        if (body || (isStreaming && i === parts.length - 1)) {
            sections.push({ title, body, isComplete: !isStreaming || i < parts.length - 1 });
        }
    }
    
    return sections;
};

/**
 * Render a markdown table with compact styling
 */
const CompactTable = ({ markdown }) => {
    return (
        <div style={{ marginTop: '4px', marginBottom: '4px', width: '100%', overflowX: 'auto' }}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                    table: ({ node, ...props }) => (
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: '0.875em',
                            border: '1px solid #d0d7de',
                            tableLayout: 'auto'
                        }} {...props} />
                    ),
                    thead: ({ node, ...props }) => <thead {...props} />,
                    tbody: ({ node, ...props }) => <tbody {...props} />,
                    tr: ({ node, ...props }) => <tr {...props} />,
                    th: ({ node, ...props }) => (
                        <th style={{
                            backgroundColor: '#0972d3',
                            color: 'white',
                            border: '1px solid #0860b8',
                            padding: '6px 8px',
                            textAlign: 'left',
                            fontWeight: 600,
                            fontSize: '0.95em',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                        }} {...props} />
                    ),
                    td: ({ node, ...props }) => (
                        <td style={{
                            border: '1px solid #d0d7de',
                            padding: '6px 8px',
                            backgroundColor: 'white',
                            fontSize: '0.9em',
                            verticalAlign: 'top',
                            wordWrap: 'break-word',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            overflow: 'hidden',
                            maxWidth: '200px'
                        }} {...props} />
                    ),
                    p: ({ node, ...props }) => <span {...props} />,
                    code: ({ node, inline, ...props }) => (
                        inline 
                            ? <code style={{
                                backgroundColor: 'rgba(175, 184, 193, 0.2)',
                                padding: '2px 4px',
                                borderRadius: '3px',
                                fontSize: '0.9em',
                                color: '#d91515',
                                wordBreak: 'break-all',
                                whiteSpace: 'normal'
                            }} {...props} />
                            : <code {...props} />
                    ),
                }}
            >
                {markdown}
            </ReactMarkdown>
        </div>
    );
};

/**
 * Render a section with title and content
 */
const Section = ({ title, body, isComplete, isStreaming, isPreamble }) => {
    // Check if body contains a table
    const hasTable = body.includes('|');
    
    // If this is a preamble (content before first heading), render with special styling
    if (isPreamble) {
        return (
            <div style={{ 
                marginBottom: '8px',
                padding: '8px 10px',
                backgroundColor: '#f0f8ff',
                border: '2px solid #0972d3',
                borderRadius: '6px',
                maxWidth: '60%',
                width: 'fit-content'
            }}>
                <Box fontSize="body-m">
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => <p style={{ fontSize: '12px', lineHeight: '1.4', margin: '2px 0' }} {...props} />,
                            strong: ({ node, ...props }) => <strong style={{ color: '#0972d3', fontWeight: 700 }} {...props} />,
                            ul: ({ node, ...props }) => <ul style={{ margin: '4px 0', paddingLeft: '20px' }} {...props} />,
                            li: ({ node, ...props }) => <li style={{ margin: '2px 0' }} {...props} />,
                            code: ({ node, inline, ...props }) => (
                                inline 
                                    ? <code style={{
                                        backgroundColor: 'rgba(9, 114, 211, 0.1)',
                                        padding: '1px 3px',
                                        borderRadius: '3px',
                                        fontSize: '11px',
                                        color: '#0972d3',
                                        fontWeight: 600
                                    }} {...props} />
                                    : <code {...props} />
                            ),
                        }}
                    >
                        {body}
                    </ReactMarkdown>
                </Box>
            </div>
        );
    }
    
    return (
        <div style={{ marginBottom: '8px' }}>
            <Box variant="h3" fontSize="heading-s" padding={{ bottom: 'xxxs' }}>
                {title}
            </Box>
            {hasTable ? (
                <CompactTable markdown={body} />
            ) : (
                <Box fontSize="body-m" padding={{ top: 'xxxs' }}>
                    <ReactMarkdown 
                        remarkPlugins={[remarkGfm]}
                        components={{
                            p: ({ node, ...props }) => <p style={{ fontSize: '14px', lineHeight: '1.6' }} {...props} />,
                            li: ({ node, ...props }) => <li style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '4px' }} {...props} />,
                            code: ({ node, inline, ...props }) => (
                                inline 
                                    ? <code style={{
                                        backgroundColor: 'rgba(175, 184, 193, 0.2)',
                                        padding: '2px 4px',
                                        borderRadius: '3px',
                                        fontSize: '13px',
                                        color: '#d91515'
                                    }} {...props} />
                                    : <code {...props} />
                            ),
                        }}
                    >
                        {body}
                    </ReactMarkdown>
                </Box>
            )}
            {!isComplete && isStreaming && (
                <Box padding={{ top: 'xxxs' }}>
                    <span style={{ color: '#0972d3' }}>●●●</span>
                </Box>
            )}
        </div>
    );
};

/**
 * Main structured response component
 */
const StructuredResponse = ({ content, isStreaming = false }) => {
    const sections = parseStructuredResponse(content, isStreaming);
    
    // Find Layer Timing Breakdown and Key Findings sections to render side-by-side
    const layerTimingIndex = sections.findIndex(s => s.title && s.title.includes('Layer Timing Breakdown'));
    const keyFindingsIndex = sections.findIndex(s => s.title && s.title.includes('Key Findings'));
    
    return (
        <SpaceBetween size="xs">
            {sections.map((section, index) => {
                // If this is Layer Timing Breakdown and Key Findings exists, render them side-by-side
                if (index === layerTimingIndex && keyFindingsIndex > layerTimingIndex) {
                    const keyFindingsSection = sections[keyFindingsIndex];
                    return (
                        <div key={index} style={{ display: 'flex', gap: '16px', width: '100%' }}>
                            <div style={{ flex: '0 0 18%', minWidth: '220px', maxWidth: '18%' }}>
                                <Section 
                                    title={section.title} 
                                    body={section.body}
                                    isComplete={section.isComplete}
                                    isStreaming={isStreaming}
                                    isPreamble={section.isPreamble}
                                />
                            </div>
                            <div style={{ flex: '0 0 32%', minWidth: 0, maxWidth: '32%' }}>
                                <Section 
                                    title={keyFindingsSection.title} 
                                    body={keyFindingsSection.body}
                                    isComplete={keyFindingsSection.isComplete}
                                    isStreaming={isStreaming}
                                    isPreamble={keyFindingsSection.isPreamble}
                                />
                            </div>
                        </div>
                    );
                }
                
                // Skip Key Findings if it was already rendered with Layer Timing
                if (index === keyFindingsIndex && layerTimingIndex >= 0 && layerTimingIndex < keyFindingsIndex) {
                    return null;
                }
                
                return (
                    <Section 
                        key={index} 
                        title={section.title} 
                        body={section.body}
                        isComplete={section.isComplete}
                        isStreaming={isStreaming}
                        isPreamble={section.isPreamble}
                    />
                );
            })}
        </SpaceBetween>
    );
};

export default StructuredResponse;
